import { HookHouseTemplate, TemplateExportFile, ImportStrategy } from '@/types/template.types';
import { fetchWithCsrf } from '@utils/fetchWithCsrf';

const STORAGE_KEY = 'hookhouse_templates';
const APP_VERSION = '3.53';
const API_BASE = '/api/studio-templates';

/**
 * Generate a UUID for template IDs
 */
export function generateTemplateId(): string {
  return crypto.randomUUID?.() ||
    `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Load all templates from API, falling back to localStorage.
 * Self-healing: if DB is empty but localStorage has templates, auto-imports them.
 */
export async function loadTemplates(): Promise<HookHouseTemplate[]> {
  try {
    const res = await fetchWithCsrf(`${API_BASE}/list`);
    if (res.ok) {
      const json = await res.json();
      if (json.data && json.data.length > 0) {
        return json.data.map((t: any) => ({
          id: t.id,
          name: t.name,
          description: t.description || '',
          icon: t.icon || '🎵',
          tags: t.tags || [],
          createdAt: t.createdAt,
          updatedAt: t.updatedAt,
          config: t.config,
        }));
      }

      // DB is empty — check if localStorage has templates to recover
      const local = loadTemplatesFromStorage();
      if (local.length > 0) {
        console.log(`[templates] DB empty but localStorage has ${local.length} templates — auto-importing...`);
        const imported = await backfillTemplates(local);
        if (imported.length > 0) return imported;
        return local; // fallback if import fails
      }

      return [];
    }
  } catch {
    // API unavailable
  }
  // Fallback: localStorage (read-only, populated by one-time migration)
  return loadTemplatesFromStorage();
}

/** Import localStorage templates into the DB (self-healing after DB migration) */
async function backfillTemplates(templates: HookHouseTemplate[]): Promise<HookHouseTemplate[]> {
  const results: HookHouseTemplate[] = [];
  for (const t of templates) {
    try {
      const res = await fetchWithCsrf(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: t.name,
          description: t.description,
          tags: t.tags,
          config: t.config,
        }),
      });
      if (res.ok) {
        const json = await res.json();
        results.push({ ...t, id: json.data?.id || t.id });
      }
    } catch {
      // Non-fatal — will retry next load
    }
  }
  console.log(`[templates] Auto-imported ${results.length}/${templates.length} templates to DB`);
  return results;
}

/**
 * Load templates from localStorage only (synchronous fallback)
 */
export function loadTemplatesFromStorage(): HookHouseTemplate[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Failed to load templates:', error);
    return [];
  }
}

/**
 * Save all templates to localStorage.
 * @deprecated No longer used for write-through; kept for TemplateContext import compatibility.
 * Will be removed in a future cleanup pass.
 */
export function saveTemplates(_templates: HookHouseTemplate[]): boolean {
  // No-op: DB is the source of truth. localStorage is read-only fallback from migration.
  return true;
}

/**
 * Add a template — POST to API
 */
export async function addTemplate(template: HookHouseTemplate): Promise<HookHouseTemplate> {
  try {
    const res = await fetchWithCsrf(`${API_BASE}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: template.name,
        description: template.description,
        tags: template.tags,
        config: template.config,
      }),
    });
    if (res.ok) {
      const json = await res.json();
      return { ...template, id: json.data.id };
    }
  } catch {
    // API failed — template stays with client-generated ID
  }
  return template;
}

/**
 * Update a template — PUT to API
 */
export async function updateTemplate(id: string, updates: Partial<HookHouseTemplate>): Promise<void> {
  await fetchWithCsrf(`${API_BASE}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: updates.name,
      description: updates.description,
      tags: updates.tags,
      config: updates.config,
    }),
  });
}

/**
 * Delete a template — DELETE from API
 */
export async function deleteTemplate(id: string): Promise<void> {
  await fetchWithCsrf(`${API_BASE}/${id}`, { method: 'DELETE' });
}

/**
 * Export templates to downloadable JSON file
 */
export function exportTemplates(templates: HookHouseTemplate[], filename?: string): void {
  const exportData: TemplateExportFile = {
    hookhouse_templates_v1: {
      exportedAt: new Date().toISOString(),
      appVersion: APP_VERSION,
      templates
    }
  };

  const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `hookhouse-templates-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export a single template
 */
export function exportSingleTemplate(template: HookHouseTemplate): void {
  const safeName = template.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  exportTemplates([template], `hookhouse-template-${safeName}.json`);
}

/**
 * Parse and validate an import file
 */
export function parseImportFile(fileContent: string): HookHouseTemplate[] | null {
  try {
    const parsed = JSON.parse(fileContent);

    // Check for versioned format
    if (parsed.hookhouse_templates_v1) {
      const { templates } = parsed.hookhouse_templates_v1;
      if (Array.isArray(templates)) {
        return templates.filter(isValidTemplate);
      }
    }

    // Fallback: maybe it's just an array of templates
    if (Array.isArray(parsed)) {
      return parsed.filter(isValidTemplate);
    }

    return null;
  } catch (error) {
    console.error('Failed to parse import file:', error);
    return null;
  }
}

/**
 * Basic validation that a template has required fields
 */
function isValidTemplate(t: any): t is HookHouseTemplate {
  return (
    typeof t === 'object' &&
    typeof t.id === 'string' &&
    typeof t.name === 'string' &&
    typeof t.config === 'object'
  );
}

/**
 * Merge imported templates with existing templates
 */
export function mergeTemplates(
  existing: HookHouseTemplate[],
  incoming: HookHouseTemplate[],
  strategy: ImportStrategy
): HookHouseTemplate[] {
  switch (strategy) {
    case 'replace_all':
      return incoming;

    case 'merge_skip':
      // Only add templates with names that don't already exist
      const existingNames = new Set(existing.map(t => t.name.toLowerCase()));
      const newOnes = incoming.filter(t => !existingNames.has(t.name.toLowerCase()));
      return [...existing, ...newOnes];

    case 'merge_overwrite':
      // Overwrite existing templates with same name, add new ones
      const result = [...existing];
      for (const inc of incoming) {
        const idx = result.findIndex(t => t.name.toLowerCase() === inc.name.toLowerCase());
        if (idx >= 0) {
          result[idx] = { ...inc, updatedAt: new Date().toISOString() };
        } else {
          result.push(inc);
        }
      }
      return result;

    default:
      return existing;
  }
}
